import React, { useMemo } from 'react';
import { useMemoizedFn } from 'ahooks';
import { CalendarCurrentBtnArrowType } from '../enums';
import { DEFAULT_CALENDAR_COL_SPACING } from '../constants';

export default function useCurrentBtn(props: {
  // 日历卡片外层容器（支持滚动那个dom）
  cardListEl?: React.RefObject<HTMLElement>;
  // 是否正在加载数据中
  isLoading?: boolean;
  // 是否无日历数据
  isNoData?: boolean;
  // 日历卡片容器是否水平方向溢出
  isOverflowX?: boolean;
  // 是否自动定位滚动结束
  isAutoScrollFinished?: boolean;
  // 列间距
  colSpacing?: number;
  // 当前时间
  current?: string;
  // 日历卡片外层容器（支持滚动那个dom）最大滚动距离
  maxScrollLeft?: number;
  // 当前时间对应的scrollLeft（即日历卡片容器的scrollLeft为该值时，当前时间则位于可视区域的最左侧）
  currentTimeScrollLeft?: number;
  // 日历卡片面板容器当前的scrollLeft值
  scrollLeft?: number;
  // 日历卡片容器最小scrollLeft（当前时间对应的日历卡片在可视区域内）
  min?: number;
  // 日历卡片容器最大scrollLeft（当前时间对应的日历卡片在可视区域内）
  max?: number;
}) {
  const {
    cardListEl,
    isLoading = true,
    isNoData = true,
    isOverflowX = false,
    isAutoScrollFinished = false,
    colSpacing,
    current,
    maxScrollLeft,
    currentTimeScrollLeft,
    scrollLeft,
    min,
    max,
  } = props;

  const colSpacingTmp = colSpacing || DEFAULT_CALENDAR_COL_SPACING;

  // 是否展示定位到当前时间节点对应的日历卡片列按钮
  const visible = useMemo<boolean>(() => {
    if (isLoading || isNoData) return false;
    if (!current) return true;
    if (!isOverflowX) return false;
    if (!isAutoScrollFinished) return false;
    if (typeof scrollLeft !== 'number' || Number.isNaN(scrollLeft)) return false;
    if (typeof min !== 'number' || Number.isNaN(min)) return false;
    if (typeof max !== 'number' || Number.isNaN(max)) return false;
    return scrollLeft < min || scrollLeft > max;
  }, [isLoading, isNoData, current, isOverflowX, isAutoScrollFinished, scrollLeft, min, max]);

  // 是否禁用定位到当前时间节点对应的日历卡片列按钮
  const disabled = useMemo<boolean>(() => {
    if (isLoading || isNoData) return false;
    return !current;
  }, [isLoading, isNoData, current]);

  /**
   * currentBtn类型
   * 0: 不展示箭头
   * -1: 展示左箭头
   * 1: 展示右箭头
   */
  const currentBtnType = useMemo<CalendarCurrentBtnArrowType>(() => {
    if (isLoading || isNoData) return CalendarCurrentBtnArrowType.Hidden;
    if (!isOverflowX) return CalendarCurrentBtnArrowType.Hidden;
    // 没有当前时间（当前日历数据不包含当前时间）
    if (!current) return CalendarCurrentBtnArrowType.Hidden;
    /** ********* 数据异常，隐藏currentBtn按钮 ********* **/
    if (typeof scrollLeft !== 'number' || Number.isNaN(scrollLeft))
      return CalendarCurrentBtnArrowType.Hidden;
    if (typeof min !== 'number' || Number.isNaN(min)) return CalendarCurrentBtnArrowType.Hidden;
    if (typeof max !== 'number' || Number.isNaN(max)) return CalendarCurrentBtnArrowType.Hidden;
    /** ********* 数据异常，隐藏currentBtn按钮 ********* **/
    // 当前时间的日历数据在可视区域右侧
    if (scrollLeft < min) return CalendarCurrentBtnArrowType.Right;
    // 当前时间的日历数据在可视区域的左侧
    if (scrollLeft > max) return CalendarCurrentBtnArrowType.Left;
    // 异常情况，不展示箭头
    return CalendarCurrentBtnArrowType.Hidden;
  }, [isLoading, isNoData, current, scrollLeft, min, max]);

  /**
   * 点击currentBtn按钮的回调
   * 滚动当前时间节点对应的列到容器最左侧
   */
  const handleCurrentBtnClick = useMemoizedFn(() => {
    if (!cardListEl?.current) return;
    if (typeof currentTimeScrollLeft !== 'number' || Number.isNaN(currentTimeScrollLeft)) return;
    if (typeof maxScrollLeft !== 'number' || Number.isNaN(maxScrollLeft)) return;
    const prevColsDistance = currentTimeScrollLeft > 0 ? currentTimeScrollLeft + colSpacingTmp : 0;
    cardListEl.current.scrollLeft = Math.min(prevColsDistance, maxScrollLeft);
  });

  return {
    // 是否展示定位到当前时间节点对应的日历卡片列按钮,
    visible,
    // 是否禁用定位到当前时间节点对应的日历卡片列按钮
    disabled,
    // currentBtn类型
    currentBtnType,
    // 点击currentBtn按钮的回调
    onCurrentBtnClick: handleCurrentBtnClick,
  };
}
